import * as React from "react";
import {BadgeCheck, Circle, CircleCheck, CircleDashed, CircleX, TriangleAlert} from "lucide-react";
import {Popover, PopoverContent, PopoverTrigger} from "./popover";
import {cn} from "../lib/utils";

// DBへの依存を持たない表示用の構造型。databaseサービスのFieldCheckStatusと同じ7値。
type FieldCheckStatus = "unverified" | "trusted_import" | "needs_review" | "confirmed" | "corrected" | "wrong" | "withdrawn";
type ActorKind = "human" | "machine" | null;
const icons = {Circle, CircleCheck, CircleDashed, CircleX, TriangleAlert, BadgeCheck};
type Presentation = {label: string; icon: keyof typeof icons | "Sansan"; color: string};

export function fieldCheckPresentation(status: FieldCheckStatus, actorKind?: ActorKind): Presentation {
  switch (status) {
    case "unverified": return {label: "未確認", icon: "Circle", color: "text-muted-foreground"};
    case "withdrawn": return {label: "未確認（戻した）", icon: "Circle", color: "text-muted-foreground"};
    case "trusted_import": return {label: "Sansan", icon: "Sansan", color: "text-info"};
    case "needs_review": return {label: "要確認", icon: "TriangleAlert", color: "text-warning"};
    case "confirmed": return actorKind === "machine"
      ? {label: "自動で一致", icon: "CircleDashed", color: "text-info"}
      : {label: "確認済み", icon: "CircleCheck", color: "text-success"};
    case "corrected": return {label: "直して確認", icon: "BadgeCheck", color: "text-success"};
    case "wrong": return {label: "誤り", icon: "CircleX", color: "text-destructive"};
  }
}

export interface FieldCheckMarkProps {
  status: FieldCheckStatus;
  actorKind?: ActorKind;
  actorRole?: "admin" | "hostess" | null;
  actorName?: string | null;
  checkedAt?: string | null;
  machineLabel?: string | null;
  detail?: Record<string, unknown> | null;
  fieldLabel: "氏名" | "住所";
  className?: string;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function FieldCheckMark({status, actorKind, actorRole, actorName, checkedAt, machineLabel, detail, fieldLabel, className}: FieldCheckMarkProps): React.ReactElement {
  const {label, icon, color} = fieldCheckPresentation(status, actorKind);
  const Icon = icon === "Sansan" ? null : icons[icon];
  const date = checkedAt ? new Date(checkedAt) : null;
  const day = date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("ja-JP", {timeZone: "Asia/Tokyo", month: "numeric", day: "numeric"}).format(date)
    : "";
  const person = actorName ?? (actorRole === "admin" ? "事務所" : "ホステス");
  const prefix = day ? `${day} ` : "";
  let message: string;
  switch (status) {
    case "unverified": message = "まだ誰も確認していません"; break;
    case "withdrawn": message = `${prefix}${person}が未確認に戻しました`; break;
    case "trusted_import": message = "Sansan から移行した名刺（人が検証済み）"; break;
    case "needs_review": message = actorKind === "machine"
      ? `${prefix}診断（${machineLabel ?? "診断名なし"}）の疑い`
      : `${prefix}${person}が「あとで見る」にしました`; break;
    case "confirmed": message = actorKind === "machine"
      ? `${prefix}診断（${machineLabel ?? "診断名なし"}）で一致（人の確認ではない）`
      : `${prefix}${person}が画像と見比べて確認`; break;
    case "corrected": {
      const before = detail?.before;
      const values = before && typeof before === "object" && !Array.isArray(before) ? before as Record<string, unknown> : {};
      const oldValue = fieldLabel === "氏名" ? text(values.name_kanji) : [text(values.postal_code), text(values.address)].filter(Boolean).join(" ");
      message = `${prefix}${person}が直して確認${oldValue ? `（直す前: ${oldValue}）` : ""}`;
      break;
    }
    case "wrong": message = `${prefix}${person}が誤りにしました`; break;
  }
  const reason = status === "needs_review" && actorKind === "machine" ? text(detail?.reason)
    : ["needs_review", "wrong", "corrected"].includes(status) ? text(detail?.note) : "";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`${fieldLabel}の確認: ${label}`} className={cn("inline-flex shrink-0 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", color, className)}>
          {Icon ? <Icon className="size-4" aria-hidden="true" /> : <span aria-hidden="true" className="inline-flex size-4 items-center justify-center rounded border border-current text-xs font-semibold">S</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="text-sm break-words">
        <p>{message}</p>
        {reason && <p className="mt-1 whitespace-pre-wrap">{reason}</p>}
      </PopoverContent>
    </Popover>
  );
}
